/* eslint-disable react-hooks/exhaustive-deps */
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { prepareHeaders } from "../user/user.api";
import { PaginationResults, TypeQuery } from "../user/user.type";
import { ApiBaseUrl } from "../../http";
import { QueryUrl, useLocationState } from '../../Utils';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ICity, IConsommable, IHistoriqueLogement, ILogement, ILogementPilote, LogementFormdata } from './logement.type';

const LogementApi = createApi({
    reducerPath: "logementApi",
    tagTypes: ["logement", "logementPilote", "demandePilote", "logementPiloteActive", "ville", "logements"],
    baseQuery: fetchBaseQuery({
        baseUrl: `${ApiBaseUrl}/api/`,
        prepareHeaders,
    }),
    endpoints: (build) => ({
        AddOrUpdateLogement: build.mutation<
            ILogement,
            { slug?: string; data: ILogement | FormData | Partial<ILogement> }
        >({
            query: ({ slug, data }) => ({
                url: slug ? `logements/${slug}/` : `logements/`,
                method: slug ? "PUT" : "POST",
                body: data,
            }),
            invalidatesTags: ["logements", "logement","logementPilote","logementPiloteActive"],
        }),
        getLogements: build.query<PaginationResults<ILogement>, TypeQuery>({
            query: (query) => QueryUrl("logements", query),
            providesTags: ["logements"],
        }),
        getLogement: build.query<ILogement, string>({
            query: (slug) => `logements/${slug}`,
            providesTags: ["logement"],
        }),
        deleteLogement: build.mutation<LogementFormdata, string>({
            query: (slug) => ({
                url: `logements/${slug}/`,
                method: "DELETE",
            }),
            invalidatesTags: ["logement", "logements"],
        }),

        getConsommableByLogement: build.query<IConsommable[], TypeQuery>({
            query: ({ slug, ...query }) => QueryUrl(`logement/${slug}/consommables/`, query),
            providesTags: ["logement"],
        }),
        getMissionPrevusByLogement: build.query<IConsommable[], TypeQuery>({
            query: ({ slug, ...query }) => QueryUrl(`logement/${slug}/mission_prevus/`, query),
            providesTags: ["logement"],
        }),
        getHistoriqueByLogement: build.query<PaginationResults<IHistoriqueLogement>, TypeQuery>({
            query: ({ slug, ...query }) => QueryUrl(`logement/${slug}/historiques/`, query),
            providesTags: ["logement"],
        }),
        getLogementsPilote: build.query<PaginationResults<ILogementPilote>, TypeQuery>({
            query: (query) => QueryUrl("logements_pilotes", query),
            providesTags: ["logementPiloteActive"],
        }),
        getDemandesPilote: build.query<PaginationResults<ILogementPilote>, TypeQuery>({
            query: (query) => QueryUrl("demandes_pilotes", query),
            providesTags: ["demandePilote"],
        }),
        getLogementPilote: build.query<ILogementPilote, string>({
            query: (slug) => `user/${slug}/demandes_pilotes`,
            providesTags: ["logementPilote"],
        }),
        AddOrUpdateLogementPilote: build.mutation<ILogementPilote,{ slug?: string; data: ILogementPilote | FormData | Partial<ILogementPilote> }>({
            query: ({ slug, data }) => ({
                url: slug ? `demandes_pilotes/${slug}/` : `demandes_pilotes/`,
                method: slug ? "PUT" : "POST",
                body: data,
            }),
            invalidatesTags: ["logementPilote","demandePilote","logementPiloteActive"],
        }),
        getVilles: build.query<ICity[], TypeQuery>({
            query: (query) => QueryUrl("logement/villes", query),
            providesTags: ["ville"],
        }),
    }),
});

export default LogementApi

export const {
    useAddOrUpdateLogementMutation,
    useGetLogementsQuery,
    useDeleteLogementMutation,
    useLazyGetLogementQuery,
    useGetConsommableByLogementQuery,
    useLazyGetConsommableByLogementQuery,
    useGetLogementsPiloteQuery,
    useGetDemandesPiloteQuery,
    useLazyGetLogementPiloteQuery,
    useAddOrUpdateLogementPiloteMutation,
    useGetMissionPrevusByLogementQuery,
    useLazyGetMissionPrevusByLogementQuery,
    useLazyGetHistoriqueByLogementQuery,
    useGetHistoriqueByLogementQuery,
    useGetVillesQuery,
    useLazyGetLogementsQuery
} = LogementApi;

export function useLogementFromLocation(): [
    ILogement,
    boolean,
    string,
    (slug: string) => any
] {
    const itemState = useLocationState<ILogement | undefined>(undefined);
    const [item, setItem] = useState(itemState);
    const { slug, lSlug } = useParams<{ slug: string; lSlug: string }>();
    const [findById, { data, isError, isLoading }] = useLazyGetLogementQuery();
    const navigate = useNavigate();

    useEffect(() => {
        if (slug && !lSlug) {
            findById(slug);
        } else if (lSlug) {
            findById(lSlug);
        }
    }, [slug, lSlug]);
    useEffect(() => {
        if (isError && !itemState) {
            navigate(-1);
        }
    }, [isError]);
    useEffect(() => {
        if (data) {
            setItem(data);
        }
    }, [data]);

    return [item as ILogement, isLoading, slug as string, findById];
}